import React from 'react';
import { ComponentComponentsFormSection } from '../../../types/schema';
import { FormSectionWrapper, LoadingContainer, SectionCard } from './styles';
import { FiCheck } from 'react-icons/fi';
import { AnswerBackend, AnswerState } from '../../../views/FormView';
import { FeedbackQuestion, FeedbackQuestionProps } from '../FeedbackQuestion';
import { FiChevronDown, FiChevronUp, FiRefreshCw } from 'react-icons/fi';
import { debounce } from 'lodash';
import { LoadingIcon } from '../LoadingIcon';

type FormSectionProps = {
  id?: string;
  section?: ComponentComponentsFormSection;
  isSubmit?: boolean;
  answers?: AnswerState;
  locale?: string;
  allQuestionsAnswered?: boolean;
  submitLoading?: boolean;
  isWizardMode?: boolean;
  showAdvanceButton?: boolean;
  sectionIndex?: number;
  currentSectionIndex?: number;
  isCurrentSection?: boolean;
  onAdvance?: () => void;
  updateAnswers?: (answer: AnswerBackend) => void;
  handleSubmit?: () => void;
  promptQuestions?: ComponentComponentsFormSection['questions'];
  loadingPromptQuestions?: boolean;
  loadingPromptResult?: boolean;
  loaderText?: string | null;
  loadPromptQuestions?: (
    sectionId: string,
    promptQuestion: string,
    promptCommon?: string | null,
  ) => void;
  promptError?: string | null;
  buttonText: string;
  questionStartIndex?: number;
  isTestMode?: boolean;
  showRegenerateButton?: boolean;
  onRegenerateResult?: () => void;
};

const SubmitIcon = ({ answered }: { answered: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.043 0.832031C13.2799 0.704427 13.5078 0.722656 13.7266 0.886719C13.9635 1.03255 14.0638 1.24219 14.0273 1.51562L12.0586 13.3281C12.0221 13.5286 11.9036 13.6836 11.7031 13.793C11.5208 13.9023 11.3385 13.9115 11.1562 13.8203L7.73828 12.3984L5.98828 14.5039C5.80599 14.7409 5.56901 14.8047 5.27734 14.6953C4.98568 14.6042 4.83984 14.4036 4.83984 14.0938V11.8789L11.4297 3.86719C11.4844 3.77604 11.4753 3.70312 11.4023 3.64844C11.3294 3.57552 11.2565 3.56641 11.1836 3.62109L3.33594 10.5391L0.4375 9.33594C0.182292 9.22656 0.0455729 9.03516 0.0273438 8.76172C0.00911458 8.48828 0.11849 8.28776 0.355469 8.16016L13.043 0.832031Z"
      fill={answered ? '#D1FFC3' : '#fafafa'}
    />
  </svg>
);

const FormSection: React.FC<FormSectionProps> = ({
  id,
  answers,
  section,
  isSubmit,
  locale = 'en',
  allQuestionsAnswered,
  submitLoading,
  isWizardMode,
  showAdvanceButton,
  onAdvance,
  sectionIndex,
  currentSectionIndex,
  isCurrentSection,
  updateAnswers,
  handleSubmit,
  promptQuestions = [],
  loadingPromptQuestions,
  loadingPromptResult,
  loaderText,
  loadPromptQuestions,
  promptError,
  buttonText,
  questionStartIndex = 0,
  isTestMode,
  showRegenerateButton,
  onRegenerateResult,
}) => {
  const [hide, setHide] = React.useState(false);
  const [_, triggerRender] = React.useState<number>(0);
  const questionsRef = React.useRef<HTMLDivElement | null>(null);
  const sectionRef = React.useRef<HTMLDivElement | null>(null);

  const hasPromptQuestion = !!section?.promptQuestion;

  const allQuestions = hasPromptQuestion ? promptQuestions || [] : section?.questions || [];

  const showBottomLine =
    !isWizardMode ||
    (sectionIndex !== undefined && sectionIndex > 0) ||
    (currentSectionIndex !== undefined && currentSectionIndex > 0);

  const isEn = locale === 'en';

  // Load prompt questions when section becomes active
  React.useEffect(() => {
    if (
      hasPromptQuestion &&
      isCurrentSection &&
      id &&
      section?.promptQuestion &&
      loadPromptQuestions &&
      !promptQuestions?.length &&
      !loadingPromptQuestions &&
      !promptError
    ) {
      loadPromptQuestions(id, section.promptQuestion, section.promptCommon);
    }
  }, [
    hasPromptQuestion,
    isCurrentSection,
    id,
    section?.promptQuestion,
    loadPromptQuestions,
    promptQuestions?.length,
    loadingPromptQuestions,
    promptError,
  ]);

  const answered = React.useMemo(() => {
    if (!answers) return false;

    return allQuestions.every(
      (q) => answers[q?.title || '']?.answer || !answers[q?.title || '']?.required,
    );
  }, [answers, allQuestions]);

  const sectionHasError = React.useMemo(() => {
    const allTitles = allQuestions.map((q) => q?.title);
    return allTitles.some((title) => !!answers?.[title as string]?.error);
  }, [answers, allQuestions]);

  React.useEffect(() => {
    const render = debounce(() => {
      triggerRender((state) => state + 1);
    }, 200);

    window.addEventListener('resize', render);

    return () => {
      window.removeEventListener('resize', render);
    };
  }, []);

  // Scroll to section title when it becomes the current active section
  React.useEffect(() => {
    if (currentSectionIndex === 0) {
      return;
    }

    if (
      isWizardMode &&
      isCurrentSection &&
      currentSectionIndex !== undefined &&
      sectionIndex === currentSectionIndex
    ) {
      setTimeout(() => {
        const navbarHeight = 120;
        const elementPosition = sectionRef.current?.getBoundingClientRect().top || 0;
        const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }, 450);
    }
  }, [isCurrentSection, currentSectionIndex, sectionIndex, isWizardMode]);

  return (
    <FormSectionWrapper
      id={id}
      answered={answered || allQuestionsAnswered}
      isSubmit={isSubmit}
      questionsMaxHeight={questionsRef.current?.scrollHeight}
    >
      {isSubmit ? (
        <>
          <div className="circle submit">
            <SubmitIcon answered={!!allQuestionsAnswered} />
          </div>
          {showRegenerateButton ? (
            <button
              type="button"
              disabled={submitLoading}
              style={{ fontSize: 14, height: 38, width: 'auto', padding: '0 18px' }}
              className="submit-button"
              onClick={onRegenerateResult}
            >
              {submitLoading ? (
                <>
                  <div className="lds-ring">
                    <div />
                    <div />
                    <div />
                    <div />
                  </div>
                </>
              ) : (
                <FiRefreshCw size={13} />
              )}
              {submitLoading ? 'Gerando...' : 'Gerar resultado novamente'}
            </button>
          ) : (
            <button
              disabled={submitLoading}
              className="submit-button"
              onClick={() => handleSubmit?.()}
            >
              {submitLoading && (
                <>
                  <div className="lds-ring">
                    <div />
                    <div />
                    <div />
                    <div />
                  </div>
                  {loaderText && <span>{loaderText}</span>}
                </>
              )}
              {!submitLoading && <>{buttonText}</>}
            </button>
          )}
        </>
      ) : (
        <>
          {showBottomLine && <div className="vertical-line" />}
          <div className="cards-container">
            <div className="section-container">
              <div className={`circle ${sectionHasError ? 'error' : ''}`}>
                {sectionHasError ? '!' : answered && <FiCheck size={25} />}
              </div>
              <SectionCard
                ref={sectionRef}
                onClick={() => {
                  if (!isSubmit && !isWizardMode) setHide(!hide);
                }}
                style={isWizardMode ? { cursor: 'default' } : undefined}
              >
                <div>
                  <h2>{section?.title}</h2>
                  <p>{section?.description}</p>
                </div>
                {!isWizardMode && (hide ? <FiChevronDown size={30} /> : <FiChevronUp size={30} />)}
              </SectionCard>
            </div>
            {isTestMode &&
              hasPromptQuestion &&
              !loadingPromptQuestions &&
              !loadingPromptResult &&
              !!promptQuestions?.length &&
              loadPromptQuestions &&
              id &&
              section?.promptQuestion && (
                <button
                  type="button"
                  style={{
                    fontSize: 13,
                    height: 32,
                    marginBottom: 8,
                    maxWidth: 220,
                    alignSelf: 'center',
                  }}
                  className="submit-button"
                  onClick={() => {
                    loadPromptQuestions(id, section.promptQuestion as string, section.promptCommon);
                    setTimeout(() => {
                      const navbarHeight = 120;
                      const elementPosition = sectionRef.current?.getBoundingClientRect().top || 0;
                      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }, 50);
                  }}
                >
                  <FiRefreshCw size={13} />
                  {isEn ? 'Resend questions' : 'Reenviar perguntas'}
                </button>
              )}
            <div ref={questionsRef} className={`questions-container ${hide ? 'hide' : ''}`}>
              {/* {promptError && (
                <ErrorContainer>
                  <FiAlertCircle size={48} />
                  <p>
                    {isEn
                      ? 'An error occurred while loading the questions.'
                      : 'Ocorreu um erro ao carregar as perguntas.'}
                  </p>
                  {loadPromptQuestions && id && section?.promptQuestion && (
                    <button
                      type="button"
                      onClick={() => loadPromptQuestions(id, section.promptQuestion as string)}
                    >
                      {isEn ? 'Try again' : 'Tentar novamente'}
                    </button>
                  )}
                </ErrorContainer>
              )} */}

              {!promptError &&
                (loadingPromptResult || (hasPromptQuestion && loadingPromptQuestions)) && (
                  <LoadingContainer>
                    <LoadingIcon />
                    <p>
                      {loaderText ||
                        (isEn ? 'Processing your answers...' : 'Processando suas respostas...')}
                    </p>
                  </LoadingContainer>
                )}

              {!promptError &&
                !loadingPromptQuestions &&
                !loadingPromptResult &&
                allQuestions.map((question, questionIndex) => (
                  <FeedbackQuestion
                    scroll={
                      !!(
                        Object.entries(answers || {}).find((value) => value[1].error)?.[0] ===
                        question?.title
                      )
                    }
                    {...(question as unknown as FeedbackQuestionProps)}
                    key={question?.title}
                    errored={!!answers?.[question?.title as string].error}
                    errorMessage={answers?.[question?.title as string].error as string}
                    updateAnswers={updateAnswers}
                    answerOptions={
                      question?.answerOptions?.map((option) => option?.title) as string[]
                    }
                    number={questionStartIndex + questionIndex + 1}
                    locale={locale}
                    maximum={question?.maximum}
                    minimum={question?.minimum}
                    allowMore={question?.allowMore}
                    steper={question?.steper}
                  />
                ))}

              {showAdvanceButton && (
                <button className="submit-button" onClick={onAdvance}>
                  {isEn ? 'Next' : 'Avançar'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </FormSectionWrapper>
  );
};

export default FormSection;
